import React, { useEffect, useState, useMemo } from 'react';
import { Box, Button, CircularProgress, Typography, Chip, Paper, Grid, TextField, MenuItem } from '@mui/material';
import { DataGrid, gridClasses, GridToolbar } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import { green, blue, orange, grey } from '@mui/material/colors';
import { useLayout } from '../../context/LayoutContext';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import EmailIcon from '@mui/icons-material/Email';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

import { useGetAgencyShiftsQuery, useSendAgencyShiftReportMutation } from '../../slices/shiftApiSlice';
import { useGetAgenciesQuery } from '../../slices/agencyApiSlice';

const StyledDataGrid = styled(DataGrid)`
  & .${gridClasses.row}.cancelledRow {
    background-color: ${orange[50]};
  }
  & .${gridClasses.row}.completeRow {
    background-color: ${green[50]};
  }
`;

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
  } catch {
    return 'Invalid Date';
  }
};

const getShiftStatus = (shift) => {
  if (shift.agencyCancelled) return 'Cancelled';
  if (shift.actualStatus === 'Complete') return 'Complete';
  if (shift.actualStatus === 'Not-Worked') return 'Not-Worked';
  return 'Open / Assigned';
};

const AgencyShiftsListScreen = () => {
  const { setFluid } = useLayout();

  const toLocalISO = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const [dateRange, setDateRange] = useState({
    startDate: toLocalISO(new Date(new Date().setMonth(new Date().getMonth() - 1))),
    endDate: toLocalISO(new Date()),
  });
  const [selectedAgencyId, setSelectedAgencyId] = useState('all');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  const { data: agenciesData } = useGetAgenciesQuery({ isActive: true });

  const { data: shifts, isLoading, isError, error, refetch } = useGetAgencyShiftsQuery({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    agencyId: selectedAgencyId === 'all' ? undefined : selectedAgencyId,
  });

  
  // Latest-to-earliest — newest/upcoming (open) shifts first, older
  // (already completed) shifts sink to the bottom.
  const sortedShifts = useMemo(() => {
    if (!shifts) return [];
    return [...shifts].sort(
      (a, b) => new Date(b.startDateTime) - new Date(a.startDateTime)
    );
  }, [shifts]);



  const [sendReport, { isLoading: sending }] = useSendAgencyShiftReportMutation();

  const selectedAgency = agenciesData?.find(a => a._id === selectedAgencyId);
  const agencyLabel = selectedAgency ? selectedAgency.agencyName : 'All Agencies';

  const stats = useMemo(() => {
    if (!sortedShifts) return { total: 0, complete: 0, cancelled: 0, open: 0 };
    return {
      total: sortedShifts.length,
      complete: sortedShifts.filter(s => getShiftStatus(s) === 'Complete').length,
      cancelled: sortedShifts.filter(s => getShiftStatus(s) === 'Cancelled').length,
      open: sortedShifts.filter(s => getShiftStatus(s) === 'Open / Assigned').length,
    };
  }, [sortedShifts]);

  const columns = [
    { field: 'wardDepartmentArea', headerName: 'Ward/Dept', width: 150 },
    {
      field: 'agencyName', headerName: 'Agency', width: 160,
      valueGetter: (params) =>
        params.row.actualFilledByAgencyName || params.row.filledByAgencyName || 'N/A',
    },
    {
      field: 'workerName', headerName: 'Worker', width: 160,
      valueGetter: (params) =>
        params.row.actualFilledByAgencyWorkerName || params.row.filledByAgencyWorkerName || 'N/A',
    },
    { field: 'categoryOfStaff', headerName: 'Category', width: 120 },
    {
      field: 'startDateTime', headerName: 'Start', width: 160,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'endDateTime', headerName: 'End', width: 160,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'poNumber', headerName: 'Ref Number', width: 150,
      renderCell: (params) => params.value
        ? <Typography variant="body2" fontWeight="bold">{params.value}</Typography>
        : <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>,
    },
    {
      field: 'status', headerName: 'Status', width: 140,
      valueGetter: (params) => getShiftStatus(params.row),
      renderCell: (params) => {
        const color = params.value === 'Cancelled' ? 'warning'
          : params.value === 'Complete' ? 'success'
          : params.value === 'Not-Worked' ? 'error'
          : 'info';
        return <Chip label={params.value} size="small" color={color} variant="outlined" />;
      },
    },
  ];

  const getRowClassName = (params) => {
    const status = getShiftStatus(params.row);
    if (status === 'Cancelled') return 'cancelledRow';
    if (status === 'Complete') return 'completeRow';
    return '';
  };

  const handleDateRangeChange = (field) => (e) => {
    setDateRange(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.warning('Please allow pop-ups to print');
      return;
    }

    const rows = sortedShifts.map(shift => {
      const status = getShiftStatus(shift);
      const statusColor = status === 'Cancelled' ? '#e65100'
        : status === 'Complete' ? '#2e7d32'
        : status === 'Not-Worked' ? '#c62828'
        : '#1565c0';
      const workerName = shift.actualFilledByAgencyWorkerName || shift.filledByAgencyWorkerName || 'N/A';
      const agName = shift.actualFilledByAgencyName || shift.filledByAgencyName || 'N/A';

      return `
        <tr>
          <td>${shift.wardDepartmentArea || ''}</td>
          <td>${agName}</td>
          <td>${workerName}</td>
          <td>${shift.categoryOfStaff || ''}</td>
          <td>${formatDate(shift.startDateTime)}</td>
          <td>${formatDate(shift.endDateTime)}</td>
          <td style="font-weight:bold;">${shift.poNumber || '—'}</td>
          <td style="color:${statusColor};font-weight:bold;">${status}</td>
        </tr>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Agency Shift Report — ${agencyLabel}</title>
        <style>
          @page { size: landscape; margin: 0.5in; }
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { border-bottom: 2px solid #000; padding-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
          th { background-color: #1976d2; color: white; }
        </style>
      </head>
      <body>
        <h1>Agency Shift Report — ${agencyLabel}</h1>
        <p>Period: ${dateRange.startDate} to ${dateRange.endDate} • Total shifts: ${sortedShifts.length}</p>
        <table>
          <thead>
            <tr><th>Ward</th><th>Agency</th><th>Worker</th><th>Category</th><th>Start</th><th>End</th><th>Ref Number</th><th>Status</th></tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <script>
          window.onload = function() { window.print(); setTimeout(() => window.close(), 100); };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleOpenEmailDialog = () => {
    setRecipientEmail(selectedAgency?.agencyEmail || '');
    setAdditionalNotes('');
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!recipientEmail.trim()) {
      toast.error('Please enter a recipient email');
      return;
    }
    try {
      const result = await sendReport({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        agencyId: selectedAgencyId === 'all' ? undefined : selectedAgencyId,
        agencyLabel,
        recipientEmail,
        additionalNotes,
      }).unwrap();
      toast.success(result.message || 'Report sent');
      setEmailDialogOpen(false);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to send report');
    }
  };

  useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }
  if (isError) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><Typography color="error">Error: {error?.data?.message || error?.message}</Typography></Box>;
  }

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', width: '100%', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Agency Shifts</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ bgcolor: '#673ab7' }}>
            Print
          </Button>
          <Button
            variant="contained"
            startIcon={<EmailIcon />}
            onClick={handleOpenEmailDialog}
            disabled={selectedAgencyId === 'all'}
            sx={{ bgcolor: '#1976d2' }}
          >
            Email Report
          </Button>
          <Button variant="outlined" onClick={() => { refetch(); toast.success('Data refreshed'); }}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Date Range</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <input type="date" value={dateRange.startDate} onChange={handleDateRangeChange('startDate')}
                style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc', width: '100%' }} />
              <input type="date" value={dateRange.endDate} onChange={handleDateRangeChange('endDate')}
                style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc', width: '100%' }} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
            <TextField
              select fullWidth label="Agency" size="small"
              value={selectedAgencyId}
              onChange={(e) => setSelectedAgencyId(e.target.value)}
            >
              <MenuItem value="all">All Agencies</MenuItem>
              {agenciesData?.map(a => (
                <MenuItem key={a._id} value={a._id}>{a.agencyName} ({a.agencyTier})</MenuItem>
              ))}
            </TextField>
            {selectedAgencyId === 'all' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Select a specific agency to enable emailing a report
              </Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Grid container spacing={1}>
            <Grid item xs={3}><Paper sx={{ p: 1.5, bgcolor: grey[700], color: 'white', textAlign: 'center' }}>
              <Typography variant="h6">{stats.total}</Typography><Typography variant="caption">Total</Typography>
            </Paper></Grid>
            <Grid item xs={3}><Paper sx={{ p: 1.5, bgcolor: green[600], color: 'white', textAlign: 'center' }}>
              <Typography variant="h6">{stats.complete}</Typography><Typography variant="caption">Complete</Typography>
            </Paper></Grid>
            <Grid item xs={3}><Paper sx={{ p: 1.5, bgcolor: orange[700], color: 'white', textAlign: 'center' }}>
              <Typography variant="h6">{stats.cancelled}</Typography><Typography variant="caption">Cancelled</Typography>
            </Paper></Grid>
            <Grid item xs={3}><Paper sx={{ p: 1.5, bgcolor: blue[600], color: 'white', textAlign: 'center' }}>
              <Typography variant="h6">{stats.open}</Typography><Typography variant="caption">Open</Typography>
            </Paper></Grid>
          </Grid>
        </Grid>
      </Grid>

      <StyledDataGrid
        density="compact"
        rows={sortedShifts}
        columns={columns}
        getRowId={(row) => row._id}
        getRowClassName={getRowClassName}
        initialState={{
          pagination: { pageSize: 25 },
          sorting: { sortModel: [{ field: 'startDateTime', sort: 'desc' }] },
        }}
        pageSizeOptions={[25, 50, 100]}
        slots={{ toolbar: GridToolbar }}
        slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } } }}
        disableSelectionOnClick
      />

      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Email Agency Shift Report
          <IconButton onClick={() => setEmailDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            <strong>Agency:</strong> {agencyLabel}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Period:</strong> {dateRange.startDate} to {dateRange.endDate} ({sortedShifts.length} shifts)
          </Typography>
          <TextField
            fullWidth label="Recipient Email" type="email" margin="normal"
            value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)}
            required
          />
          <TextField
            fullWidth label="Additional Notes (optional)" margin="normal" multiline rows={3}
            value={additionalNotes} onChange={(e) => setAdditionalNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSendEmail} disabled={sending}>
            {sending ? 'Sending...' : 'Send Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgencyShiftsListScreen;